import React, { useState } from "react";
import * as PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import Modal from "@material-ui/core/Modal";
import Link from "@material-ui/core/Link";
import AccordionPanel from "./AccordionPanel";
import CountryGroupsList from "./CountryGroupsList";
import HyperLink from "../widgets/HyperLink";
import Map from "./Map";
import Reference from "../widgets/Reference";
import Aside from "../widgets/Aside";
import methodFrequencyInfographic from "../../images/method_frequency_infographic.svg";
import graph from "../../images/icons/graph.svg";
import SourceGraphic from "../themes/infographics/source/SourceGraphic";
import "./Accordion.scss";
import styleConstants from "../../styleConstants";

const styles = {
  methodFrequencyInfographic: {
    maxWidth: 464
  },
  inlineButton: {
    fontSize: "1rem",
    fontWeight: styleConstants.fontWeights.medium,
    color: styleConstants.colors.themeBlue,
    verticalAlign: "baseline"
  },
  modalContainer: {
    position: "absolute",
    width: "90vw",
    height: "90vh",
    top: "5vh",
    left: "5vw",
    backgroundColor: "#ffffff",
    outline: 0,
    padding: "1rem"
  }
};

const Accordion = props => {
  const [selectedPanel, setSelectedPanel] = useState(
    props.initialSelectedPanel
  );

  const handleChange = panel => {
    setSelectedPanel(panel === selectedPanel ? undefined : panel);
  };

  const [modalOpen, setModalOpen] = useState(false);

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  return (
    <div className="Accordion">
      <AccordionPanel
        title="Data Sources"
        selected={selectedPanel === "data-sources"}
        onSelected={() => handleChange("data-sources")}
      >
        <p>
          This report is built on the power of partnership. Drawing on data
          provided by partners across the reproductive health community, the CGA
          is able to create the most comprehensive picture possible for each
          country. This wide array of sources helps to shape country specific
          patterns in contraceptive use.
        </p>
        <Aside icon={graph}>
          Explore where our data comes from and how it is used{" "}
          <Link
            component="button"
            underline="always"
            classes={{ button: props.classes.inlineButton }}
            onClick={handleModalOpen}
          >
            here
          </Link>
          .
        </Aside>
        <Modal open={modalOpen} onClose={handleModalClose}>
          <div className={props.classes.modalContainer}>
            <SourceGraphic />
          </div>
        </Modal>
      </AccordionPanel>

      <AccordionPanel
        title="Relative versus Absolute"
        selected={selectedPanel === "relative-versus-absolute"}
        onSelected={() => handleChange("relative-versus-absolute")}
      >
        <p>
          Throughout the CGA 2019, results are presented one of two ways:
          relative or absolute. Both provide useful but distinct ways to
          understand how things vary within categories and across all
          categories. Where relevant, figures can be toggled to switch between
          showing results as relative (% of each element) or absolute (whole
          numbers). Take, for example, a figure showing users by method,
          comparing the public versus private sector. The relative view should
          show the method mix within each sector&mdash;e.g. what proportion of
          public sector users rely on injectables as compared to private sector
          users. This is useful for understanding patterns within each sector
          and how they differ. However, because different numbers of women
          access their contraception from the public and private sectors, the
          relative view does not tell us about how many public versus private
          sector injectable users there are. Changing this figure to the
          absolute view would show this difference.
        </p>

        <p>
          Please note that in some instances relative results may not sum to
          100% due to rounding.
        </p>
      </AccordionPanel>

      <AccordionPanel
        title="GNI Groups"
        selected={selectedPanel === "gni-groups"}
        onSelected={() => handleChange("gni-groups")}
      >
        <p>
          In 2016 and 2018, the CGA published results for the full set of 135
          <Reference marker={1}>
            China and Venezuela&mdash;both of which are categorized as
            upper-middle-income countries&mdash;are not included in our
            analysis. Three countries (Micronesia, Kosovo, and American Samoa)
            are included in our totals; however, no estimates of contraceptive
            use are available for these countries, so they do not contribute to
            the estimates. However, given the relatively small sizes of these
            countries populations, their exclusion is unlikely to impact the
            aggregate results.
          </Reference>
          LMICs as well as the subset of 69 FP2020 countries. This year, we take
          a new approach. In this report we segment the 135 LMICs into three GNI
          groups as defined by the World Bank
          <Reference marker={2}>
            The World Bank segments countries into four income groups based on
            their Gross National Income (GNI) per capita. For their 2018
            classifications, countries are grouped as follows: low-income
            &lt;$995, lower-middle-income $996&ndash;$3,895, upper-middle-income
            $3,896&ndash;12,055, high-income &gt;$12,055.
          </Reference>
          : low-income countries (LIC), lower-middle-income countries (L-MIC),
          and upper-middle-income countries (U-MIC). This segmentation allows
          for visibility into differential patterns across each GNI group and
          can help focus discussions on the different needs of countries at
          different income levels. Throughout the report, results are presented
          in two ways. Comparisons of data sets are made across the three GNI
          groups (e.g. what share of implant users live in each GNI group) as
          well as within each GNI group (e.g. method mix of users within each
          GNI group). These two different perspectives provide useful insights
          into differences across the GNI groups.
        </p>
        <p>
          It is important to keep in mind, however, that while this segmentation
          provides a useful lens for viewing various patterns, there can still
          be great variation across the countries comprising each group.
        </p>
        <Map />
      </AccordionPanel>

      <AccordionPanel
        title="Country Groups"
        selected={selectedPanel === "country-groups"}
        onSelected={() => handleChange("country-groups")}
      >
        <CountryGroupsList />
      </AccordionPanel>

      <AccordionPanel
        title="Public Sector Spending"
        selected={selectedPanel === "public-sector-spending"}
        onSelected={() => handleChange("public-sector-spending")}
      >
        <p>
          Public sector spending is estimated based on expenditures by public
          sector entities. Levels of public sector spending are averaged to
          minimize the inevitable fluctuations in annual spending level that
          occur as a result of the timing or size of procurement orders, and to
          leverage multiple data sources
          <Reference marker={3}>
            Multiple data sources are available providing estimates of public
            sector spending on commodities by both donors and governments. In
            some cases, data sources overlap (e.g. multiple estimates for the
            same country and year); however, these estimates frequently don’t
            match. Generally, we look at an average of all the available data
            (multiple sources plus multiple years of data). However, in some
            cases, some data was excluded in favor of other data deemed more
            reliable.
          </Reference>
          which provide estimates for different years.
        </p>
        <p>
          Public sector spending is divided into donors and governments. Donor
          funding includes a relatively small amount of spending by NGOs ($14.8
          million, or 9%). Donor spending was calculated based on three years’
          worth of data on donor spending (2016&ndash;2018) and four years’
          worth of data on government spending (2014&ndash;2017).
        </p>
        <p>
          Government spending comprises data from 127 countries, which is 55
          more countries than were available to us in the CGA 2018. In addition,
          we have improved estimates of government spending for some countries,
          most notably India. These new figures should not be interpreted as new
          or additional spending but rather spending that was previously
          unreported.
        </p>
        <p>
          We have also modified our definition of government spending to include
          basket funds and loans, two categories of funding source previously
          considered in 2016 and 2018 CGA reports as donor funding. This amounts
          to an additional $55 million of spending in the CGA 2019 now
          classified as government, instead of donor. This reclassification of
          spending from donors to governments, coupled with more recent data
          showing declines in donor spending means that our overall donor
          spending estimate for 2019 is lower than in CGA 2018.
        </p>
        <p>
          As a result of all these changes in data sources and greater data
          visibility, the spending estimates in the CGA 2019 report are not
          comparable to the estimates in the 2016 and 2018 reports. Values from
          the three reports, therefore, should not be used to form a trend in
          spending over time.
        </p>
      </AccordionPanel>

      <AccordionPanel
        title="User Projections"
        selected={selectedPanel === "user-projections"}
        onSelected={() => handleChange("user-projections")}
      >
        <p>
          Our estimate of the total number of users of modern contraception
          comprises individual estimates for each of the 135 LMICs. For the
          subset of 69 FP2020 countries, projections are aligned with the values
          published in the 2018 FP2020 Progress Report
          <Reference marker={4}>
            FP2020.{" "}
            <HyperLink href="http://progress.familyplanning2020.org/">
              Catalyzing Collaboration (2017-2018).
            </HyperLink>
          </Reference>
          up to 2020. For the remaining non-FP2020 countries total user
          estimates are based on the median variant projection from the UN
          Population Division (UNPD).
          <Reference marker={5}>
            Based on: (1) Mark Wheldon, Vladim&iacute;ra Kantorov&aacute;,
            Philipp Ueffing and Aisha N. Z. Dasgupta (2018). Methods for
            estimating and projecting key family planning indicators among all
            women of reproductive age. United Nations, Department of Economic
            and Social Affairs, Population Division, Technical Paper No. 2. New
            York: United Nations; (2) United Nations, Department of Economic and
            Social Affairs, Population Division (2017). World Population
            Prospects: The 2017 Revision; (3) United Nations, Department of
            Economic and Social Affairs, Population Division (2018). World
            Contraceptive Use by Marital Status and Age 2018.
          </Reference>
          For 2021 to 2030 for the 69 FP2020 countries, a hybrid trend is
          created by adding the additional users from the UNPD trend to the 2020
          user estimate in each country.
        </p>
        <p>
          Both the FP2020 and UNPD estimates are created using a statistical
          model
          <Reference marker={6}>
            The Family Planning Estimation Tool (FPET) was designed to produce
            annual estimates of the contraceptive prevalence rate (CPR) and
            other indicators using statistical modeling that incorporates survey
            data and service statistics. For more information, see{" "}
            <HyperLink href="http://track20.org/download/pdf/Track20%20Technical%20Briefs/english/Technical%20Brief_FPET%20(2015%2004%2013).pdf">
              Technical Brief: Family Planning Estimation Tool.
            </HyperLink>
          </Reference>
          which uses all available survey data from Demographic and Health
          Surveys (<HyperLink href="https://dhsprogram.com/">DHS</HyperLink>),
          Multiple Indicator Cluster surveys (
          <HyperLink href="http://mics.unicef.org/">MICS</HyperLink>
          ), <HyperLink href="http://www.pma2020.org/">PMA2020</HyperLink>, and
          national surveys,
          <Reference marker={7}>
            United Nations, Department of Economic and Social Affairs,
            Population Division (2018). World Contraceptive Use 2018.
          </Reference>
          as well as high-quality service statistics in select countries.
        </p>
      </AccordionPanel>

      <AccordionPanel
        title="Users by Method"
        selected={selectedPanel === "users-by-method"}
        onSelected={() => handleChange("users-by-method")}
      >
        <p>
          Data from DHS, MICs, PMA2020 and national surveys are also used to
          estimate the number of users of each contraceptive method. For each
          country a starting method mix is taken from the most recently survey.
          From there, the method mixes for each subsequent year (from date of
          survey to 2030) is calculated by applying regional or sub-regional
          annual method mix changes calculated from the most recent two surveys
          available.
          <Reference marker={8} trailingSpace={false}>
            Calculations are done separately for married and unmarried users;
            though the CGA2019 only reports on the aggregated totals not by
            marital status. The average annual change analysis was limited to
            countries with a survey after 2005 to reflect more recent shifts in
            method mix; a total of 270 individual surveys feed into the change
            analysis. Patterns of change for married method mix are calculated
            for sub-regions (e.g. Southern Asia, Eastern Africa), while for
            unmarried method mix aggregate regions (e.g. Asia, Africa) are used
            due to more limited data availability.
          </Reference>
        </p>
      </AccordionPanel>

      <AccordionPanel
        title="Users by Sector"
        selected={selectedPanel === "users-by-sector"}
        onSelected={() => handleChange("users-by-sector")}
      >
        <p>
          Users of each method are then further sub-divided based on the source
          of their supplies (from either the public sector or one of multiple
          private sector sources
          <Reference marker={9} trailingSpace={false}>
            For the purpose of this analysis all non-public sources are
            classified as private. This includes a small number of sources such
            as shops and acquaintances that may not otherwise be considered
            private sector.
          </Reference>
          ) using the most recent DHS in a country or a sub-regional average. A
          new feature of CGA 2019 is the distinction between private sector
          users who purchase subsidized products and those who purchase
          non-subsidized products. This was done based on data shared by DKT,
          PSI and MSI
          <Reference marker={10}>
            These three organizations shared detailed data related to services
            provided and products distributed by country and channel. We are
            grateful for them sharing both this data, and their expertise in
            order to make this new analysis possible.
          </Reference>
          as well as publicly available data in the{" "}
          <HyperLink href="https://www.dktinternational.org/contraceptive-social-marketing-statistics/">
            Social Marketing Statistics
          </HyperLink>{" "}
          published annually by DKT. For each method, country specific market
          shares of subsidized product were calculated by dividing the volume of
          subsidized products provided by NGOs and SMOs by the CGA 2019 estimate
          of total private sector consumption. These calculations did not
          include the full set of services or products provided by NGOs and
          SMOs, but rather limited this to subsidized products that were
          ultimately provided through private sector channels.
          <Reference marker={11}>
            Data was removed for delivery channels likely being picked up under
            &ldquo;public sector&rdquo; (e.g. mobile outreach services provided
            within public sector clinics and sales of commodities to
            governments), and for countries where programs are largely
            commercial, meaning little or no donor subsidy was attached to the
            provision (e.g. DKT Philippines have almost no donor funding).
          </Reference>
        </p>
        <p>
          Where possible, estimates were validated against other sources. For
          example, we found that our market share estimates largely aligned with
          the SHOPS Plus{" "}
          <HyperLink href="https://shopsplusproject.org/sites/default/files/resources/C%C3%B4te d%E2%80%99Ivoire Family Planning Private Health Sector Assessment_0.pdf">
            Family Planning Private Sector Assessment in Cote d’Ivoire
          </HyperLink>{" "}
          and their finding that &ldquo;[&hellip;] with the exception of
          condoms, inexpensive socially marketed products dominate the [private]
          contraceptive market, with shares as high as 90 to 100%.&rdquo;
        </p>
      </AccordionPanel>

      <AccordionPanel
        title="Consumption Cost"
        selected={selectedPanel === "consumption-cost"}
        onSelected={() => handleChange("consumption-cost")}
      >
        <p>
          The consumption cost reflects the consumption quantity of supplies
          multiplied by their price. This includes both the cost of the
          contraceptive commodity itself as well as associated clinical
          supplies. The consumption cost does not include other cost factors
          such as fees paid for necessary medical services or required visits,
          taxes, freight, or tariffs, nor does it capture the effects of
          inflation or fluctuations in currency exchange rates.
          <Reference marker={12}>
            However, in the case of private sector consumption costs some of
            these factors may be inherently included in the price that the
            client is ultimately charged at the point of sale.
          </Reference>
        </p>
        <p>
          To produce our public sector consumption cost estimates, we used data
          from the Guttmacher Institute’s annual Adding It Up
          <Reference marker={13}>
            Jacqueline E. Darroch, Singh S., Weissman E. Adding it Up: The Costs
            and Benefits of Investing in Sexual and Reproductive Health in 2014.
            Guttmacher Institute, 2016.
          </Reference>
          report, as well as additional information concerning eight Latin
          American and Caribbean (LAC) countries that was obtained through an
          RHSC survey that asked governments to identify the prices of
          contraceptive commodities they procure.
          <Reference marker={14}>
            RHSC LAC survey of Honduras, El Salvador, Guatemala, Nicaragua,
            Mexico, Paraguay, Bolivia, Peru.
          </Reference>
          The Guttmacher analysis takes into account variations in the price
          paid in the public sector for contraceptive commodities and associated
          clinical supplies in a country, as well as the mix of different
          products (e.g. types of implants) used, to produce an average cost per
          method for each country. The public sector consumption cost also
          includes the value of the subsidy for subsidized products purchased by
          users in the private sector.
          <Reference marker={15} trailingSpace={false}>
            This was calculated as the difference between the public sector
            price and the private subsidized price for each product; therefore,
            sharing the total cost between the two sectors. In total $44.2
            million in subsidy costs was allocated into public sector
            consumption costs in 2018.
          </Reference>
        </p>
        <p>
          To produce our private sector consumption cost estimates, we used a
          range of data sources
          <Reference marker={16}>
            Data sources include: IQVIA, FP Watch, SHOPS Plus Private Sector
            Assessments, Track20 M&E Officer retail audits (unpublished), DHS
            analysis on prices paid by private sector users, PMA2020 analysis on
            prices paid by private sector users.
          </Reference>
          to estimate country-specific (or, where data was not available,
          regional) price points for both subsidized and non-subsidized products
          for four methods with sufficient data: implants, IUDs, injectables,
          and pills.
          <Reference marker={17}>
            Since most data sources do not distinguish between the prices of
            subsidized and non-subsidized products, any price point that was
            below the public sector price was considered to be subsidized. This
            assumption may not hold true in some specific situations but will
            generally capture the variation in price points between subsidized
            and non-subsidized products. Where multiple data sources existed,
            the average price across sources was used.
          </Reference>
          For the remaining methods, we used the public sector prices for all
          private sector consumption.
          <Reference marker={18}>
            This was done for sterilization, and other methods due to limited
            data availability. For condoms there is large variation in price
            across brands and it is difficult to accurately capture this
            variation; therefore, using a single price for all condom
            consumption was the more conservative approach.
          </Reference>
        </p>
      </AccordionPanel>

      <AccordionPanel
        title="Method Mix by Use and Cost"
        selected={selectedPanel === "method-mix-by-use-and-cost"}
        onSelected={() => handleChange("method-mix-by-use-and-cost")}
      >
        <div>
          The CGA 2019 report analyzes method mix in two ways: by use and by
          cost. The user method mix shows the percentage of all users of modern
          contraception that use each method. The cost method mix shows the
          relative cost of the quantity of supplies consumed of each method.
          Method use and cost are disaggregated by the six most prevalent
          contraceptive methods and a seventh category representing the least
          used methods, called other
          <Reference marker={19} trailingSpace={false}>
            &ldquo;Other&rdquo; methods of contraception include, where data are
            available, female condom, emergency contraception, Standard Days
            Method, LAM, spermicide, and other barrier methods.
          </Reference>
          :
          <div className="two-column">
            <div className="body-list">
              <h5 className="body-list-header">
                Long-acting and permanent methods (LAPMs){" "}
              </h5>
              <ul>
                <li>Sterilization (male and female)</li>
                <li>Implants</li>
                <li>IUDs</li>
              </ul>
            </div>
            <div className="body-list">
              <h5 className="body-list-header">Short-term methods (STMs)</h5>
              <ul>
                <li>Injectables</li>
                <li>Pills</li>
                <li>
                  Condoms (male; for contraception only)
                  <Reference marker={20} trailingSpace={false}>
                    The CGA only quantities condoms used primarily for
                    contraception; therefore, estimates do not represent the
                    full condom market. For current spending estimates, only 30%
                    of spending on condom procurement is included as the
                    remainder is allocated to HIV/STI related spending (based on
                    UNFPA Supplies guidance).
                  </Reference>
                </li>
                <li>Other</li>
              </ul>
            </div>
          </div>
        </div>
      </AccordionPanel>

      <AccordionPanel
        title="Consumption Quantity"
        selected={selectedPanel === "consumption-quantity"}
        onSelected={() => handleChange("consumption-quantity")}
      >
        <p>
          The consumption quantity is the amount of supplies that a user of
          contraception must personally consume over the course of a year to
          avoid becoming pregnant, multiplied by the number of users. Variation
          in the frequency with which different methods are consumed is
          especially important when comparing the method mixes of use versus
          cost.
        </p>

        <div>
          <img
            src={methodFrequencyInfographic}
            alt="method frequency infographic"
            className={props.classes.methodFrequencyInfographic}
          />
        </div>

        <p>
          Different approaches were used to estimate consumption quantities for
          short-term versus long-acting and permanent methods. Users of
          short-term methods must consume multiple products each year to obtain
          a full year of coverage. By contrast, a most users of implants, IUDs,
          and sterilizations will have no need to consume any supplies in the
          current year. Based on average duration of use the CGA estimates the
          volume of LAPM supplies that users will consume each year.
        </p>
        <p>
          Consumption quantities are attributed to either the public or private
          sector, with the private sector further subdivided into subsidized and
          non-subsidized, based on the user estimates by sector described above.
        </p>
        <p>
          Please note that consumption quantities are different from procurement
          volumes bought by institutional purchasers, which may be above or
          below the quantities needed for user consumption.
          <Reference marker={21} trailingSpace={false}>
            Procurement volume may reflect a number of factors in addition to
            user consumption, such as the volume necessary to fill supply
            pipelines and maintain adequate inventory levels from central
            warehouses to individual service delivery points. Procurement
            volumes may take into account the volume of supplies already present
            or on order, inventory holding policies along the supply chain, and
            wastage or &ldquo;leakage&rdquo; of supplies at various levels.
          </Reference>
        </p>
      </AccordionPanel>
    </div>
  );
};

Accordion.propTypes = {
  initialSelectedPanel: PropTypes.oneOf([
    "data-sources",
    "relative-versus-absolute"
  ])
};

export default withStyles(styles)(Accordion);
